import type { ZoneTypeDefinition } from "daedalus-core";

/**
 * Zones (ADR-054's amendment of 2026-09-27): the five zone types OdysseyTales registers with Daedalus, in the
 * order that gives each its overlay category, and the only place they get meaning, `ZONE_RULES`: each type's
 * rule bits, which the map server folds into one byte a collision cell so every question the tick asks of a
 * zone is one index and one mask. Also the PvP flag rule a cell's bits drive (Bible 6.1 and 6.2 as amended,
 * ADR-073's amendment of 2026-09-27).
 */

/**
 * The PvP flag byte (PROTOCOL.md 5.2): a state in the low six bits, `Switching` in bit 6 while a flag switch is in its
 * blink window (ADR-076's amendment of 2026-09-28), and the zone lock in bit 7.
 */
export const PvpFlag = { Protected: 0, Flagged: 1, Combatant: 2, Switching: 0x40, ZoneLocked: 0x80 } as const;
export type PvpFlag = (typeof PvpFlag)[keyof typeof PvpFlag];

/**
 * A zone's rule bits (decision 8): `Lock` sets Flagged on entry and locks it until exit (Bible 6.1's zone lock);
 * `NoPvp` shelters every character who is not Combatant from player harm outside a duel; `NoCreatures` keeps
 * creatures out: none spawns, stands or fights there, and no blow crosses its edge between a creature and a
 * character.
 */
export const ZoneRule = { Lock: 1, NoPvp: 2, NoCreatures: 4 } as const;
export type ZoneRule = (typeof ZoneRule)[keyof typeof ZoneRule];

/**
 * The zone types, in registration order: Sanctuary (category 1), Guarded (2), Contested (3), Hostile (4) and
 * PvP only (5). A type added later goes at the end, so no painted type changes colour. Each carries the line the
 * Zones palette shows as its tooltip, written from `ZONE_RULES` and Bible 6.1 and 6.2, and Guarded is the type
 * unpainted ground plays as (ADR-054's amendment of 2026-10-01): its rules now, and its reward multipliers when
 * they land.
 */
export const ZONE_TYPES: readonly ZoneTypeDefinition[] = [
  { id: "sanctuary", label: "Sanctuary", description: "Safe ground: outside a duel only Combatants can be hurt, and no creatures. Switch back to Protected here, in 5 s. Paint Guarded inside for a fighting pocket." },
  { id: "guarded", label: "Guarded", description: "Default for unpainted ground. Fights need both flagged or Combatant; full rewards when Protected, more when flagged. Paint in a Sanctuary for a fighting pocket.", unpainted: true },
  { id: "contested", label: "Contested", description: "Flag rules as Guarded, with higher rewards for the flagged than Guarded; full rewards when Protected." },
  { id: "hostile", label: "Hostile", description: "Everyone inside is flagged and stays flagged until they leave; open PvP and the highest rewards." },
  { id: "pvp-only", label: "PvP only", description: "Players fight under the flag rules; no creatures spawn or fight." },
];

/**
 * Each type's rule bits. Guarded and Contested have none: flag-based PvP, the rule of unpainted ground; they
 * differ only in Bible 6.2's reward multipliers.
 */
export const ZONE_RULES: Readonly<Record<string, number>> = {
  sanctuary: ZoneRule.NoPvp | ZoneRule.NoCreatures,
  guarded: 0,
  contested: 0,
  hostile: ZoneRule.Lock,
  "pvp-only": ZoneRule.NoCreatures,
};

/**
 * The flag byte's state bits, below the switching bit and the zone lock.
 */
export const PVP_STATE_MASK = 0x3f;

/**
 * The state a PvP flag byte holds, `PvpFlag.Protected`, `Flagged` or `Combatant`, whatever its switching bit and
 * zone lock say.
 *
 * @param flag The flag byte.
 * @returns The state bits.
 * @example
 * flagStateOf(PvpFlag.Flagged | PvpFlag.Switching);
 * // 1
 */
export function flagStateOf(flag: number): number {
  return flag & PVP_STATE_MASK;
}

/**
 * A zone type's rule bits.
 *
 * @param id The type's id.
 * @returns The bits.
 * @throws Error when this build gives the type no rules, so a world naming it is refused rather than played as
 *   open ground.
 */
export function zoneRulesOf(id: string): number {
  if (!Object.hasOwn(ZONE_RULES, id)) {
    throw new Error(`zone type ${id} has no rules in this build`);
  }
  return ZONE_RULES[id] as number;
}

/**
 * The flag a character not Combatant rests at in a cell: zone-locked Flagged where the cell locks, Protected in
 * a Sanctuary whatever the preference, otherwise the preference, Protected or Flagged (ADR-073's amendment of
 * 2026-09-27). A respawn, a death, the end of a Combatant window and a step all land here.
 *
 * @param zone The cell's rule bits.
 * @param preference The character's PvP preference, `PvpFlag.Protected` or `PvpFlag.Flagged`.
 * @returns The flag byte to hold.
 * @example
 * restingFlag(ZoneRule.NoPvp | ZoneRule.NoCreatures, PvpFlag.Flagged);
 * // 0: Protected in a Sanctuary
 */
export function restingFlag(zone: number, preference: number): number {
  if ((zone & ZoneRule.Lock) !== 0) {
    return PvpFlag.Flagged | PvpFlag.ZoneLocked;
  }
  return (zone & ZoneRule.NoPvp) !== 0 ? PvpFlag.Protected : preference;
}

/**
 * The PvP flag a character holds after a step onto a cell (Bible 6.1's zone lock, ADR-073's amendment of
 * 2026-09-27): a Combatant stays Combatant, the lock following the cell, and anyone else takes the cell's
 * resting flag, so entering a Hostile zone flags and locks, leaving it returns to the preference at once, and
 * entering a Sanctuary makes a character who is not fighting Protected. The switching bit is carried as it came,
 * since the combat phase settles it for the tick (ADR-076's amendment of 2026-09-28).
 *
 * @param flag The current flag byte.
 * @param zone The rule bits of the cell the character now stands on.
 * @param preference The character's PvP preference, `PvpFlag.Protected` or `PvpFlag.Flagged`.
 * @returns The flag byte to hold.
 */
export function flagAfterZone(flag: number, zone: number, preference: number): number {
  const switching = flag & PvpFlag.Switching;
  if (flagStateOf(flag) === PvpFlag.Combatant) {
    return ((zone & ZoneRule.Lock) !== 0 ? PvpFlag.Combatant | PvpFlag.ZoneLocked : PvpFlag.Combatant) | switching;
  }
  return restingFlag(zone, preference) | switching;
}

/**
 * The effect a character bears through its safe passage out of a safe zone (ADR-076's amendment of 2026-10-03): its
 * duration is the passage's length, the one knob, and the owner's status row and an attacker's target frame show it
 * with its time left.
 */
export const SAFE_PASSAGE = "safe_passage";

/**
 * Whether a step leaves shelter for open ground, which starts or resumes a safe passage (ADR-076's amendment of
 * 2026-10-03): from a cell that bars PvP, sheltered (Protected) before the step and open to player combat (Flagged, with
 * or without a Hostile zone's lock) after it. A Combatant stays Combatant across the edge and never has one, and a step
 * from open ground into a Hostile zone is not leaving shelter.
 *
 * @param fromZone The rule bits of the cell the character left.
 * @param before Its flag byte before the step.
 * @param after The flag byte the step gives it (`flagAfterZone`).
 * @returns True when the step leaves shelter.
 * @example
 * leavesShelter(ZoneRule.NoPvp | ZoneRule.NoCreatures, PvpFlag.Protected, PvpFlag.Flagged);
 * // true
 */
export function leavesShelter(fromZone: number, before: number, after: number): boolean {
  return (fromZone & ZoneRule.NoPvp) !== 0 && flagStateOf(before) === PvpFlag.Protected && flagStateOf(after) === PvpFlag.Flagged;
}

/**
 * The flag byte a character holds where it stands while its safe passage counts (ADR-076's amendment of 2026-10-03):
 * where its resting flag would be Flagged, Protected with the switching bit and the resting flag's zone lock, so no
 * player harms it or is harmed by it, it earns the Protected rate, and every page draws its mark alternating toward
 * Flagged as it draws a switch toward Flag on; anywhere else (in a Sanctuary, or with Protected off a Hostile zone)
 * its resting flag, which is Protected already.
 *
 * @param resting The resting flag at its feet (`restingFlag`), with or without the switching bit.
 * @returns The byte to hold.
 * @example
 * passageFlag(PvpFlag.Flagged | PvpFlag.ZoneLocked);
 * // 0xC0: Protected, switching, zone-locked
 */
export function passageFlag(resting: number): number {
  if (flagStateOf(resting) !== PvpFlag.Flagged) {
    return resting;
  }
  return PvpFlag.Protected | PvpFlag.Switching | (resting & PvpFlag.ZoneLocked);
}

/**
 * Whether a creature may stand on a cell: not in a Sanctuary or PvP only. A spawn position on such a cell is
 * refused, a creature's movement treats the cell as solid, and a creature drops a target who stands on one.
 *
 * @param zone The cell's rule bits.
 * @returns True when a creature may stand there.
 */
export function creatureMayStand(zone: number): boolean {
  return (zone & ZoneRule.NoCreatures) === 0;
}
